import { useEffect, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { todayKey } from '../lib/datetime'
import { safeStorage } from '../lib/safeStorage'

// Pink, not the amber every other celebration toast uses — birthday is the one
// personal (non-achievement) moment, per the Moment Marks design pass's
// Celebration.dc.html (Jason 2026-08-06: admin-lab birthday button hadn't picked
// this up when the rest of the toast polish shipped 15.892).
export const BIRTHDAY_ACCENT_RGB = '255,107,157'
export const BIRTHDAY_CONFETTI = ['🎉', '🎈', '✨', '🎂', '🌟']
const BURST_COLORS = ['#ff6b9d', '#f5c842', '#a29bfe', '#74b9ff']

// ── Birthday celebration ────────────────────────────────────────────────────
// profiles.birthday_month/day are optional and year-free (Settings → Profile).
// On any mount where today matches, show ONE full-screen cinematic — no push
// notification, no year ever collected or inferred (Jason 2026-08-02). Fires at
// most once per calendar year per device via a localStorage marker, keyed by
// year so it naturally re-arms the following birthday.
//
// v2 (2026-08-15 notification/celebration audit): a once-a-year, personal
// occasion earns its own full-screen moment instead of routing through the
// bottom Celebration toast queue — the one deliberate exception to the shared
// TOAST_SHELL consolidation that fix touched everywhere else. Trigger/gating
// logic is unchanged; only the presentation layer moved off nextep:milestone.
export function BirthdayCelebration({ profile }) {
  const [show, setShow] = useState(false)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    if (!profile?.birthday_month || !profile?.birthday_day) return
    const today = todayKey()          // YYYY-MM-DD in the user's timezone
    const [y, m, d] = today.split('-').map(Number)
    if (m !== profile.birthday_month || d !== profile.birthday_day) return

    const seenKey = `nextep_bday_seen_${y}`
    if (safeStorage.get(seenKey)) return
    safeStorage.set(seenKey, '1')
    setShow(true)
  }, [profile?.birthday_month, profile?.birthday_day])

  const bursts = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    left: (10 + Math.random() * 80).toFixed(0) + '%',
    top: (10 + Math.random() * 55).toFixed(0) + '%',
    color: BURST_COLORS[i % BURST_COLORS.length],
    delay: (i * 0.35).toFixed(2) + 's',
  })), [])

  const dismiss = () => {
    setLeaving(true)
    setTimeout(() => setShow(false), 300)
  }

  if (!show) return null

  const name = (profile.display_name || '').trim().split(/\s+/)[0] || null

  return createPortal((
    <div onClick={dismiss} style={{
      position: 'fixed', inset: 0, zIndex: 'var(--z-celebration)',
      background: 'radial-gradient(ellipse at 50% 30%, rgba(255,107,157,0.1), var(--bg) 70%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontFamily: 'var(--font)', cursor: 'pointer', overflow: 'hidden',
    }} className={leaving ? 'nxa-bday-out' : 'nxa-bday-in'}>
      {/* Firework bursts — a colored dot plus offset shadows faking a spark burst */}
      {bursts.map((b, i) => (
        <div key={i} aria-hidden style={{
          position: 'absolute', left: b.left, top: b.top, width: 6, height: 6, borderRadius: 'var(--radius-full)',
          background: b.color,
          boxShadow: `0 0 0 1px ${b.color}, 14px -8px 0 -1px ${b.color}, -14px -8px 0 -1px ${b.color}, 14px 8px 0 -1px ${b.color}, -14px 8px 0 -1px ${b.color}, 0 16px 0 -1px ${b.color}`,
          '--nxa': `bdaySparkPulse 2.4s ease-in-out ${b.delay} infinite`,
        }} />
      ))}

      <div style={{
        fontSize: 44, marginBottom: 10,
        filter: 'drop-shadow(0 0 20px rgba(255,107,157,0.6))',
      }} className={leaving ? undefined : 'nxa-bday-cake'}>🎂</div>

      <div style={{
        color: '#ff6b9d', fontSize: 11, fontWeight: 800, letterSpacing: '0.24em', textTransform: 'uppercase',
        marginBottom: 10, '--d': '0.25s',
      }} className={leaving ? undefined : 'nxa-bday-up'}>A Year Older, Same Great Taste</div>

      <div style={{
        fontFamily: 'var(--font-serif)', color: '#fff', fontWeight: 600, fontSize: 30, lineHeight: 1.15,
        marginBottom: 10, '--d': '0.35s',
      }} className={leaving ? undefined : 'nxa-bday-up'}>Happy Birthday!</div>

      <div style={{
        color: 'var(--text-dim)', fontSize: 14, marginBottom: 28,
        '--d': '0.45s',
      }} className={leaving ? undefined : 'nxa-bday-up'}>{name ? `Have a great one, ${name} 🎈` : 'Have a great one 🎈'}</div>

      <button onClick={(e) => { e.stopPropagation(); dismiss() }} style={{
        padding: '12px 26px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer',
        background: 'linear-gradient(135deg, #ff6b9d, #ff8fb3)', color: 'var(--amber-ink)',
        fontWeight: 800, fontSize: 13.5, fontFamily: 'var(--font)',
        '--d': '0.55s',
      }} className={leaving ? undefined : 'nxa-bday-up'}>Thanks!</button>

      {/* Floating balloon/confetti row along the bottom */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 90, display: 'flex', justifyContent: 'space-around', pointerEvents: 'none' }} aria-hidden>
        {BIRTHDAY_CONFETTI.map((e, i) => (
          <span key={i} style={{ fontSize: 22, '--nxa': `bdayFloat 3s ease-in-out ${(i * 0.3).toFixed(2)}s infinite` }}>{e}</span>
        ))}
      </div>

      <style>{`
        @keyframes bdayFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes bdayFadeOut { from{opacity:1} to{opacity:0} }
        @keyframes bdayCakeIn  { from{opacity:0;transform:translateY(-16px) scale(0.7)} to{opacity:1;transform:translateY(0) scale(1)} }
        @keyframes bdayUp      { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes bdaySparkPulse { 0%,100%{opacity:0.25;transform:scale(0.85)} 50%{opacity:1;transform:scale(1.15)} }
        @keyframes bdayFloat   { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
      `}</style>
    </div>
  ), document.body)
}
